import { forwardRef, type InputHTMLAttributes } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: {
    checkbox: 'w-4 h-4',
    icon: 'w-3 h-3',
    label: 'text-sm',
  },
  md: {
    checkbox: 'w-5 h-5',
    icon: 'w-3.5 h-3.5',
    label: 'text-base',
  },
  lg: {
    checkbox: 'w-6 h-6',
    icon: 'w-4 h-4',
    label: 'text-lg',
  },
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    const styles = sizeStyles[size];

    return (
      <div className={`relative flex items-start ${className}`}>
        <div className="flex items-center h-6">
          <div className="relative">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              disabled={disabled}
              className="sr-only peer"
              {...props}
            />
            <div
              className={`
                ${styles.checkbox}
                rounded border-2 transition-all duration-150
                flex items-center justify-center
                bg-white dark:bg-surface-800
                peer-focus-visible:ring-2 peer-focus-visible:ring-accent-500 peer-focus-visible:ring-offset-2
                peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-surface-900
                peer-disabled:opacity-50 peer-disabled:cursor-not-allowed
                ${error
                  ? 'border-danger-500'
                  : 'border-surface-300 dark:border-surface-600 peer-checked:border-accent-500 peer-checked:bg-accent-500'
                }
              `}
            >
              <CheckIcon
                className={`
                  ${styles.icon}
                  text-white opacity-0 peer-checked:opacity-100
                  transition-opacity duration-150
                `}
              />
            </div>
          </div>
        </div>

        {(label || description) && (
          <label
            htmlFor={checkboxId}
            className={`ml-3 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {label && (
              <span className={`block font-medium text-surface-900 dark:text-surface-100 ${styles.label}`}>
                {label}
              </span>
            )}
            {description && (
              <span className="block text-sm text-surface-500 dark:text-surface-400 mt-0.5">
                {description}
              </span>
            )}
          </label>
        )}

        {error && (
          <p className="absolute -bottom-5 left-0 text-sm text-danger-600 dark:text-danger-400">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
